import { DataOutputStream, FileSystem } from './java/DataStream';
import { AudioPlayer } from './AudioPlayer';

export class Settings {

  private static readonly FILE = 'dotch_settings.dat';

  // Audio
  private static musicVolume = 0.10;
  private static sfxVolume = 0.50;

  // Visuals
  private static screenShake = 2;      // 0=Off, 1=Low, 2=High
  private static particleDensity = 2;  // 0=Low, 1=Medium, 2=High
  private static showFps = true;
  private static playerTrail = true;
  private static gridDots = true;
  private static colorblindMode = false;

  // General
  private static language = 0; // 0=ENG, 1=NLD, 2=DEU

  static init(): void {
    Settings.load();
    // Sync music volume to AudioPlayer on startup
    AudioPlayer.setVolume(Settings.musicVolume);
  }

  // ===== Audio =====

  static getMusicVolume(): number { return Settings.musicVolume; }
  static setMusicVolume(v: number): void {
    Settings.musicVolume = Settings.clamp(v);
    AudioPlayer.setVolume(Settings.musicVolume);
    Settings.save();
  }

  static getSfxVolume(): number { return Settings.sfxVolume; }
  static setSfxVolume(v: number): void {
    Settings.sfxVolume = Settings.clamp(v);
    Settings.save();
  }

  // ===== Visuals =====

  static getScreenShake(): number { return Settings.screenShake; }
  static setScreenShake(v: number): void { Settings.screenShake = Math.max(0, Math.min(2, v)); Settings.save(); }

  static getParticleDensity(): number { return Settings.particleDensity; }
  static setParticleDensity(v: number): void { Settings.particleDensity = Math.max(0, Math.min(2, v)); Settings.save(); }

  static getShowFps(): boolean { return Settings.showFps; }
  static setShowFps(v: boolean): void { Settings.showFps = v; Settings.save(); }

  static getPlayerTrail(): boolean { return Settings.playerTrail; }
  static setPlayerTrail(v: boolean): void { Settings.playerTrail = v; Settings.save(); }

  static getGridDots(): boolean { return Settings.gridDots; }
  static setGridDots(v: boolean): void { Settings.gridDots = v; Settings.save(); }

  static getColorblindMode(): boolean { return Settings.colorblindMode; }
  static setColorblindMode(v: boolean): void { Settings.colorblindMode = v; Settings.save(); }

  // ===== General =====

  static getLanguage(): number { return Settings.language; }
  static setLanguage(v: number): void { Settings.language = Math.max(0, Math.min(2, v)); Settings.save(); }

  // ===== Screen shake multiplier for game code =====

  static getShakeMultiplier(): number {
    switch (Settings.screenShake) {
      case 0: return 0;
      case 1: return 0.5;
      case 2: return 1;
      default: return 0.5;
    }
  }

  // ===== Particle density multiplier for game code =====

  static getParticleDensityMultiplier(): number {
    switch (Settings.particleDensity) {
      case 0: return 0.4;
      case 1: return 1;
      case 2: return 1.5;
      default: return 1;
    }
  }

  // ===== Persistence =====

  private static save(): void {
    try {
      const out = new DataOutputStream(Settings.FILE);
      out.writeInt(1); // version
      out.writeFloat(Settings.musicVolume);
      out.writeFloat(Settings.sfxVolume);
      out.writeInt(Settings.screenShake);
      out.writeInt(Settings.particleDensity);
      out.writeBoolean(Settings.showFps);
      out.writeBoolean(Settings.playerTrail);
      out.writeBoolean(Settings.gridDots);
      out.writeBoolean(Settings.colorblindMode);
      out.writeInt(Settings.language);
      out.close();
    } catch {
      // Silent fail
    }
  }

  private static load(): void {
    const in_ = FileSystem.open(Settings.FILE);
    if (in_ === null) return;
    try {
      const version = in_.readInt();
      if (version >= 1) {
        Settings.musicVolume = Settings.clamp(in_.readFloat());
        Settings.sfxVolume = Settings.clamp(in_.readFloat());
        Settings.screenShake = Math.max(0, Math.min(2, in_.readInt()));
        Settings.particleDensity = Math.max(0, Math.min(2, in_.readInt()));
        Settings.showFps = in_.readBoolean();
        Settings.playerTrail = in_.readBoolean();
        Settings.gridDots = in_.readBoolean();
        Settings.colorblindMode = in_.readBoolean();
        Settings.language = Math.max(0, Math.min(5, in_.readInt()));
      }
      in_.close();
    } catch {
      // Corrupted — use defaults
      Settings.resetToDefaults();
    }
  }

  static resetToDefaults(): void {
    Settings.musicVolume = 0.10;
    Settings.sfxVolume = 0.50;
    Settings.screenShake = 2;
    Settings.particleDensity = 2;
    Settings.showFps = true;
    Settings.playerTrail = true;
    Settings.gridDots = true;
    Settings.colorblindMode = false;
    Settings.language = 0;
    AudioPlayer.setVolume(Settings.musicVolume);
    Settings.save();
  }

  private static clamp(v: number): number {
    return Math.max(0, Math.min(1, v));
  }
}
